<template>
  <main class="app">
    <div class="app-inner" :class="envClass ? [envClass] : ''">
      <RouterView v-slot="{ Component }">
        <transition :name="routeTransitionName">
          <component :is="Component" :key="route.fullPath" />
        </transition>
      </RouterView>

      <transition name="dock-fade">
        <Footer
          v-if="isFooterRoute"
          class="app-footer"
          color="white"
          :isActiveLabel="footerActiveLabel"
        />
      </transition>

      <AppInstallPopup />
      <AppToast />
      <AppAlert />
    </div>
  </main>
</template>

<script setup lang="ts">
import AppInstallPopup from '@/components/popups/AppInstallPopup.vue'
import { onMounted, onBeforeUnmount, computed, inject, ref, watch } from "vue";
import AppToast from "@/components/ui/AppToast.vue";
import AppAlert from "@/components/ui/AppAlert.vue";
import Footer from "@/components/Footer.vue";
import { usePushNotifications } from "@/composables/usePushNotifications";
import { useRoute, useRouter } from "vue-router";
import { useConversationsStore } from "@/stores/conversations";

const { attachListeners, detachListeners, syncStatus } = usePushNotifications();
const router = useRouter();
const route = useRoute();
const conversationsStore = useConversationsStore();
const routeTransitionName = ref("blur");

const footerRouteOrder = [
  "/likes",
  "/profile",
  "/foundation/search",
  "/under-nav/messenger",
  "/reels",
];

function getFooterRouteIndex(path: string) {
  const footerPath = path === "/top-profiles" ? "/likes" : path;
  return footerRouteOrder.findIndex((routePath) => footerPath === routePath);
}

function getTransitionRouteIndex(path: string) {
  if (path === "/likes") return 0;
  if (path === "/top-profiles") return 1;

  const footerIndex = getFooterRouteIndex(path);
  return footerIndex === -1 ? -1 : footerIndex + 10;
}

const isFooterRoute = computed(() => getFooterRouteIndex(route.path) !== -1);
const footerActiveLabel = computed(() => ({
  messenger: conversationsStore.hasUnread,
  likes: conversationsStore.newLikesCount > 0,
  profile: false,
  cards: false,
  reels: false,
}));

function getPathWithoutQuery(fullPath: string) {
  return fullPath.split(/[?#]/)[0];
}

watch(
  () => route.fullPath,
  (toFullPath, fromFullPath) => {
    const toPath = getPathWithoutQuery(toFullPath);
    const fromPath = getPathWithoutQuery(fromFullPath);

    if (fromPath === "/likes" && toPath === "/top-profiles") {
      routeTransitionName.value = "likes-tab-route-left";
      return;
    }

    if (fromPath === "/top-profiles" && toPath === "/likes") {
      routeTransitionName.value = "likes-tab-route-right";
      return;
    }

    const toIndex = getTransitionRouteIndex(toPath);
    const fromIndex = getTransitionRouteIndex(fromPath);

    if (toIndex === -1 || fromIndex === -1 || toIndex === fromIndex) {
      routeTransitionName.value = "blur";
      return;
    }

    routeTransitionName.value = toIndex > fromIndex ? "footer-slide-left" : "footer-slide-right";
  }
);

// iOS zoom prevent
interface TouchEventWithScale extends TouchEvent {
  scale?: number
}
function preventIosZoom(e: TouchEventWithScale) {
  if (e.scale && e.scale !== 1) e.preventDefault();
}

onMounted(() => {
  if (launchEnv === 'browser') {
    document.documentElement.classList.add('browser-page');
  }
  document.addEventListener('touchmove', preventIosZoom, { passive: false });

  // OneSignal инициализируется асинхронно в main.ts — ждём события готовности
  window.addEventListener("onesignal:ready", onOneSignalReady, { once: true });
  // Если OneSignal уже готов к моменту mount (повторный рендер) — подключаемся сразу
  if ((window as any).OneSignal?.initialized) {
    attachListeners();
    syncStatus();
  }

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", onSwMessage);
  }
});

function onOneSignalReady() {
  attachListeners();
  syncStatus();
}

function onSwMessage(event: MessageEvent) {
  if (event.data?.type === "PUSH_NAVIGATE" && event.data.url) {
    router.push(event.data.url);
  }
}

onBeforeUnmount(() => {
  if (launchEnv === 'browser') {
    document.documentElement.classList.remove('browser-page');
  }
  document.removeEventListener('touchmove', preventIosZoom as any);
  window.removeEventListener("onesignal:ready", onOneSignalReady);
  detachListeners();
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.removeEventListener("message", onSwMessage);
  }
});

type LaunchEnv = "telegram" | "pwa" | "browser";

const launchEnv = inject<LaunchEnv>('launchEnv', 'browser');

const envClass = computed(() => ({
  'app--pwa': launchEnv === 'pwa',
  'app--telegram': launchEnv === 'telegram',
  'app--browser': launchEnv === 'browser',
}));

</script>

<style>
.app {
  position: relative;
  height: 100%;
  overflow: hidden;
  background: var(--bg-primary);
}

:global(body) {
  margin: 0;
  background: var(--bg-primary);
}

.app-inner {
  position: relative;
  height: 100%;
  overflow: hidden;
}

.offset-pwa {
  margin-top: 25px !important;
}

.offset-tg {
  margin-top: 70px !important;
}

.offset-tg-sm {
  margin-top: 50px !important;
}

.offset-tg-md {
  margin-top: 60px !important;
}

.offset-padding {
  padding-top: 40px !important;
}

.padding-24 {
  padding-top: 24px !important;
}

/* Анимация переходов между страницами */
.blur-enter-from {
  filter: blur(10px);
  opacity: 0;
}

.blur-enter-to {
  filter: blur(0);
  opacity: 1;
}

.blur-leave-from {
  filter: blur(0);
  opacity: 1;
}

.blur-leave-to {
  filter: blur(10px);
  opacity: 0;
}

.blur-enter-active,
.blur-leave-active {
  transition: all 0.4s ease;
}

.footer-slide-left-enter-active,
.footer-slide-left-leave-active,
.footer-slide-right-enter-active,
.footer-slide-right-leave-active {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  backface-visibility: hidden;
  transform: translateZ(0);
  transition:
    transform 500ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 500ms ease,
    filter 500ms ease;
  will-change: transform, opacity;
}

.footer-slide-left-enter-active,
.footer-slide-right-enter-active {
  z-index: 2;
}

.footer-slide-left-leave-active,
.footer-slide-right-leave-active {
  z-index: 1;
}

.footer-slide-left-enter-from {
  transform: translate3d(100%, 0, 0);
  opacity: 1;
  filter: blur(0);
}

.footer-slide-left-leave-to {
  transform: translate3d(-30%, 0, 0) scale(0.992);
  opacity: 0.96;
  filter: blur(0.5px);
}

.footer-slide-right-enter-from {
  transform: translate3d(-100%, 0, 0);
  opacity: 1;
  filter: blur(0);
}

.footer-slide-right-leave-to {
  transform: translate3d(30%, 0, 0) scale(0.992);
  opacity: 0.96;
  filter: blur(0.5px);
}

.footer-slide-left-enter-to,
.footer-slide-left-leave-from,
.footer-slide-right-enter-to,
.footer-slide-right-leave-from {
  transform: translate3d(0, 0, 0);
  opacity: 1;
  filter: blur(0);
}

.app-footer {
  z-index: 1000;
}

.dock-fade-enter-active,
.dock-fade-leave-active {
  transition:
    opacity 220ms ease,
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}

.dock-fade-enter-from,
.dock-fade-leave-to {
  opacity: 0;
  transform: translate3d(-50%, 12px, 0) scale(0.98);
}

.likes-tab-route-left-enter-active,
.likes-tab-route-left-leave-active,
.likes-tab-route-right-enter-active,
.likes-tab-route-right-leave-active {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.likes-tab-route-left-enter-active,
.likes-tab-route-right-enter-active {
  z-index: 2;
}

.likes-tab-route-left-leave-active,
.likes-tab-route-right-leave-active {
  z-index: 1;
}

.likes-tab-page-content {
  backface-visibility: hidden;
  transform: translateZ(0);
  will-change: transform, opacity, filter;
}

.likes-tab-route-left-enter-active .likes-tab-page-content,
.likes-tab-route-left-leave-active .likes-tab-page-content,
.likes-tab-route-right-enter-active .likes-tab-page-content,
.likes-tab-route-right-leave-active .likes-tab-page-content {
  transition:
    transform 360ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 300ms ease,
    filter 300ms ease;
}

.likes-tab-route-left-enter-from .likes-tab-page-content {
  transform: translate3d(26px, 0, 0);
  opacity: 0;
  filter: blur(1px);
}

.likes-tab-route-left-leave-to .likes-tab-page-content {
  transform: translate3d(-18px, 0, 0);
  opacity: 0;
  filter: blur(1px);
}

.likes-tab-route-right-enter-from .likes-tab-page-content {
  transform: translate3d(-26px, 0, 0);
  opacity: 0;
  filter: blur(1px);
}

.likes-tab-route-right-leave-to .likes-tab-page-content {
  transform: translate3d(18px, 0, 0);
  opacity: 0;
  filter: blur(1px);
}

.likes-tab-route-left-enter-to .likes-tab-page-content,
.likes-tab-route-left-leave-from .likes-tab-page-content,
.likes-tab-route-right-enter-to .likes-tab-page-content,
.likes-tab-route-right-leave-from .likes-tab-page-content {
  transform: translate3d(0, 0, 0);
  opacity: 1;
  filter: blur(0);
}

@media (min-width: 768px) {
  .footer-slide-left-leave-to,
  .footer-slide-right-leave-to {
    transform: translate3d(0, 0, 0) scale(0.992);
    opacity: 0;
    filter: blur(1.5px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .blur-enter-active,
  .blur-leave-active,
  .footer-slide-left-enter-active,
  .footer-slide-left-leave-active,
  .footer-slide-right-enter-active,
  .footer-slide-right-leave-active,
  .likes-tab-route-left-enter-active,
  .likes-tab-route-left-leave-active,
  .likes-tab-route-right-enter-active,
  .likes-tab-route-right-leave-active,
  .likes-tab-route-left-enter-active .likes-tab-page-content,
  .likes-tab-route-left-leave-active .likes-tab-page-content,
  .likes-tab-route-right-enter-active .likes-tab-page-content,
  .likes-tab-route-right-leave-active .likes-tab-page-content {
    transition: none !important;
  }
}

html.browser-page,
html.browser-page body,
html.browser-page #app {
  height: 100svh;
  overflow: hidden;
}
</style>
